import { Careers } from 'codegen/schema'
import { Layout } from 'components/layout'
import { RichTextContainer } from 'components/rich-text-content/rich-text-content.styles'
import { careersIndexQuery } from 'lib/queries'
import { getClient, overlayDrafts } from 'lib/sanity.server'
import { withAuthUser } from 'next-firebase-auth'
import styled from 'styled-components'
import { urlWithSiteOrigin } from 'utils/url-with-site-origin'

const CardWrapper = styled.div`
  max-width: 75rem;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  justify-content: center;
  grid-gap: 1rem;
  padding: 1rem;
  background-color: ${({ theme }) => theme.palette.background.paper};

  @media (max-width: 64rem) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 43.75rem) {
    grid-template-columns: 1fr;
  }
`

const Header = styled.h1`
  max-width: 75rem;
  margin: 0 auto;
  padding: 1rem;
`

const Card = styled.div`
  background-color: ${({ theme }) => theme.palette.background.default};
  border-radius: 2px;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.spacing(0.5)};

  @media (max-width: 43.75rem) {
    border-radius: ${({ theme }) => theme.spacing(1)};
  }
`

const CardImageWrapper = styled.div`
  position: relative;
  height: 240px;
`

const CardBottom = styled.div`
  padding: 1rem;
`

const CardTag = styled.span`
  display: inline-block;
  color: ${({ theme }) => theme.palette.primary.main};
  border: 1px solid ${({ theme }) => theme.palette.primary.main};
  border-radius: 2px;
  padding: 0 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  height: 1.625rem;

  &:not(:last-child) {
    margin-right: 0.5rem;
  }
`

const CardTitle = styled.a`
  display: block;
  text-decoration: none;
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 1.125rem;
  font-weight: 600;
  height: 3.375rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &:hover {
    text-decoration: underline;
  }
`

interface IndexProps {
  preview?: boolean
  allCareersPosts?: Careers[]
}

function Index({ allCareersPosts, preview }: IndexProps) {
  const hasJobPost = allCareersPosts && allCareersPosts?.length > 0

  return (
    <>
      <Layout
        seoProps={{
          title: 'Read interesting careers about productivity and improvement',
          description:
            'Read the careers that will provide you with various information related to touch typing and productivity improvement',
          canonicalUrl: urlWithSiteOrigin('/careers/'),
          pageUrl: urlWithSiteOrigin('/careers/'),
          noIndex: true,
        }}
      >
        {hasJobPost && (
          <>
            <Header>Careers</Header>
            <CardWrapper>
              {allCareersPosts.map((post: Careers) => {
                // TODO: n: the image was rendered using this https://www.sanity.io/docs/image-url but it was not working
                // therefore, for now the src was read directly from sanity data and used it like https://www.sanity.io/docs/image-url#custom-cdn-domains
                // please verify what is the best approach to render images
                const title = post?.title

                return (
                  <Card key={post.title} data-cy="careers-card">
                    {preview && (
                      <div>
                        <b>Preview mode!</b>
                      </div>
                    )}

                    <CardBottom>
                      <div>
                        {post.tags?.map((tag, index) => {
                          return <CardTag key={index}>{tag.label}</CardTag>
                        })}
                      </div>

                      <CardTitle title={`Go to ${title} page`} href={`/careers/${post.slug}/`}>
                        {title}
                      </CardTitle>
                    </CardBottom>
                  </Card>
                )
              })}
            </CardWrapper>
          </>
        )}

        {!hasJobPost && (
          <RichTextContainer>
            <h1>Careers</h1>

            <p>
              Thank you for your interest in joining our team at TypingMentor! We are dedicated to fostering a culture
              of growth, innovation, and collaboration, and we appreciate your enthusiasm to be a part of our journey.
            </p>

            <p>
              At this moment, we do not have any job openings available. However, we encourage you to check back
              regularly, as our team is always evolving and new opportunities may arise in the future.
            </p>

            <p>
              In the meantime, we invite you to stay connected with us through our social media channels and subscribe
              to our newsletter to receive updates about our company and any future job postings.
            </p>

            <p>
              Once again, thank you for considering TypingMentor as a potential employer. We wish you the best of luck
              in your job search and hope to have the opportunity to work with you in the future.
            </p>
          </RichTextContainer>
        )}
      </Layout>
    </>
  )
}

export async function getStaticProps({ preview = false }) {
  const allCareersPosts = overlayDrafts(await getClient(preview).fetch(careersIndexQuery))
  return {
    props: { allCareersPosts, preview },
  }
}

export default withAuthUser<IndexProps>()(Index)
